import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "react-redux";
import {
  Navigate,
  Outlet,
  RouteProps,
  generatePath,
  useLocation,
  useOutlet,
  useParams,
} from "react-router-dom";
import { AppContainer } from "../common/components";
import { useSelector } from "../common/hooks/useSelector";
import { useCart } from "../utils/CartHooks";
import { ROUTES } from "../constant/common.constant";
import { useUserInfoFetcher } from "../utils/UserHooks";

type Props = RouteProps & {
  wrappedByAppContainer?: boolean;
};

export const PrivateRoutes = (props: Props) => {
  const { wrappedByAppContainer } = props;
  const location = useLocation();
  const locationState = location.state;
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const outlet = useOutlet();
  const { dispatch } = useStore();
  const params = useParams();
  const { fetchCartItemsAndUpdateStore } = useCart();
  const { initUserInfo } = useUserInfoFetcher();

  //When user will be logged in, userInfos will be set and valid
  //If not, then no point in calling the other APIs
  const userInfos = useSelector((state) => state.userInfo);

  //If userInfos is not set it can be because we are not logged in yet
  //or that the user is using the url directly withhout going through the login page
  //If it's the latter, so we must check if the user is logged in and set the userInfos in the store
  useEffect(() => {
    let mounted = true;

    initUserInfo().finally(() => {
      if (mounted) {
        setIsCheckingSession(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, [dispatch, initUserInfo]);

  const initCart = useCallback(async () => {
    if (!userInfos || _.isEmpty(userInfos) || !userInfos.id) {
      return;
    }

    return fetchCartItemsAndUpdateStore();
  }, [fetchCartItemsAndUpdateStore, userInfos]);

  useEffect(() => {
    initCart();
  }, [initCart]);

  if (isCheckingSession) {
    return null;
  }

  if (!userInfos?.id) {
    return (
      <Navigate
        to={{
          pathname: generatePath(
            params.token && !locationState?.inviteLinkExpired
              ? ROUTES.LOGIN_WITH_TOKEN
              : ROUTES.LOGIN,
            {
              token:
                locationState?.inviteLinkExpired || !params.token
                  ? undefined
                  : params.token,
            }
          ),
        }}
      />
    );
  }

  if (!outlet?.props) {
    return <Navigate to={ROUTES.HOME} />;
  }

  return wrappedByAppContainer ? (
    <AppContainer>
      <Outlet />
    </AppContainer>
  ) : (
    <Outlet />
  );
};
